import React, { useEffect, useMemo, useRef } from 'react';
import { StyleSheet, ScrollView, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import MetaHead from '@/components/MetaHead';
import AffiliateProgram from '@/components/Settings/AffiliateProgram';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthStore } from '@/store';

const createStyles = (colors, isXS, insetBottom = 0) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.solid_color_primary,
  },
  contentWrapper: {
    width: '100%',
    flex: 1,
    flexDirection: 'column',
  },
  scrollContainer: {
    flex: 1,
    width: '100%',
  },
  scrollContainerContent: {
    width: '100%',
    paddingBottom: insetBottom + 40,
  },
  content: {
    // Keep the side margins inside the scrollable area (same rule as /referral).
    maxWidth: 880,
    padding: isXS ? 20 : 24,
    paddingTop: isXS ? 10 : 15,
    alignSelf: 'center',
    width: '100%',
  },
});

// Partner-only dashboard. Anonymous visitors and signed-in users without a
// partner record see the info card inside AffiliateProgram; anonymous
// visitors never stay on the page — we route them back or to /app, so the
// URL never sits on an orphaned screen.
const AffiliatePage = React.memo(() => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();

  const styles = useMemo(
    () => createStyles(colors, isXS, insets.bottom),
    [colors, isXS, insets.bottom]
  );

  return (
    <View style={styles.container}>
      <MetaHead
        title={t('pages.affiliate.title')}
        description={t('pages.affiliate.description')}
        noindex
      />
      <View style={styles.contentWrapper}>
        <ScrollView
          style={styles.scrollContainer}
          contentContainerStyle={styles.scrollContainerContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.content}>
            <AffiliateProgram />
          </View>
        </ScrollView>
      </View>
    </View>
  );
});

export default function AffiliateRoute() {
  const router = useRouter();
  const token = useAuthStore(state => state.token);
  const isTokenExpired = useAuthStore(state => state.isTokenExpired);
  const isAuthenticated = !!token && !isTokenExpired();
  const opened = useRef(false);

  // The affiliate program is a per-partner dashboard; an anonymous
  // deep-link visitor has nothing to see, so leave the route quietly.
  useEffect(() => {
    if (isAuthenticated) return;
    if (opened.current) return;
    opened.current = true;
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/app');
    }
  }, [isAuthenticated, router]);

  if (!isAuthenticated) return null;

  return <AffiliatePage />;
}
